import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDidUpdate } from 'rooks';
import pluralize from 'pluralize';
import { Col, Row, Spin } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';
import queryString from 'query-string';

import WorkerCard from '../WorkerCard';
import { getDuplicatesWorkers } from 'redux/reducers/workerSlice';
import { WorkerItemDetailViewModel } from 'api/adminApiClient';
import BaseSearch from 'components/wrappers/BaseSearch';
import { useAppDispatch } from 'hooks/useAppDispatch';

export default function DuplicateWorkersSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { query, section } = queryString.parse(location.search);
  const dispatch = useAppDispatch();
  const [workers, setWorkers] = useState<WorkerItemDetailViewModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const infiniteScrollParentRef = useRef(null);
  const latestRequestId = useRef(0);

  useDidUpdate(() => {
    const queryData = {
      query,
      section,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [query]);

  useEffect(() => {
    initInfinityScroll();
  }, [query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setWorkers([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetWorkers = useCallback(
    (result) => {
      const loadedWorkers = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newWorkers = [...workers, ...loadedWorkers];
      const hasMoreRows = newWorkers.length < totalCount;

      setHasMore(hasMoreRows);
      setWorkers(newWorkers);
      setTotalCount(totalCount);
    },
    [workers],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getDuplicatesWorkers({
          pageNumber,
          searchQuery: query ? query.trim() : undefined,
          requestId,
        }),
      );
      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getDuplicatesWorkers.fulfilled.match(result)) {
          onSetWorkers(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetWorkers, query],
  );

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24} md={6}>
              <BaseSearch
                searchQuery={query}
                placeholder="Search by worker name, ID"
              />
            </Col>
            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">
                      {pluralize('duplicate worker', totalCount, true)}
                    </p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">
                      There are no duplicate workers.
                    </span>
                  )}
                </>
              )}
            </Col>
          </Row>

          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {workers.map((worker, index) => (
                  <Col
                    md={24}
                    lg={12}
                    className="d-flex"
                    key={`${worker.id}-${index}`}
                  >
                    <WorkerCard worker={worker} />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
